<template>
	<span class="tooltip">
		<!-- If small form factor and modal prop is true, opens tooltip in a modal -->
		<template v-if="openModalOnClick">
			<span
				ref="container"
				tabindex="0"
				role="button"
				:class="[
					labelClasses,
					'tooltip-component no-outline-when-tabbing',
					{ 'tooltip-active': active },
					{ 'tooltip-modal': openModalInSmall }
				]"
				data-tooltip-trigger
				:aria-describedby="id"
				@click="onClickToOpenModal"
				@keyup.enter="onClickToOpenModal"
			>
				<span class="outline-when-tabbing" v-html="text" />
			</span>
		</template>

		<!-- Otherwise, open tooltip in tooltip bubble -->
		<template v-else-if="hasMessage || hasCustomTooltipSlot">
			<span
				ref="container"
				tabindex="0"
				role="button"
				class="tooltip-component no-outline-when-tabbing"
				:class="[labelClasses, { 'tooltip-active': active }]"
				data-tooltip-trigger
				:aria-describedby="id"
				@focus="onFocus"
				@click="onClick"
				@blur="onBlur"
				@mouseenter="onMouseEnter"
				@keyup.enter="onClick"
			>
				<span class="outline-when-tabbing" v-html="text" />
				<span class="tooltip-container" ref="tt_container">
					<!-- Use this slot if you need to include components or have more control over the html and css classes -->
					<span
						v-if="hasCustomTooltipSlot"
						@click="(e) => e.stopPropagation()"
						@touchend="(e) => e.stopPropagation()"
						:id="id"
						role="tooltip"
						:class="[widthCSS, alignCSS, placement]"
						ref="tooltip"
					>
						<slot name="customTooltip" />
					</span>
					<!-- otherwise it will use the data from the message prop (passed in through title attribute on span)  -->
					<span
						v-else-if="message"
						@click="(e) => e.stopPropagation()"
						@touchend="(e) => e.stopPropagation()"
						:id="id"
						role="tooltip"
						:class="[widthCSS, alignCSS, placement]"
						ref="tooltip"
						v-html="message"
					/>
					<span class="tooltip-arrow" aria-hidden="true" />
				</span>
			</span>
		</template>

		<template v-else>
			<span v-html="text" />
		</template>
	</span>
</template>

<script>
import Vue from "vue";
import { EventBus } from "@vg-faset/core";
import { UniqueId } from "../../../utilities";
// import Styles from "@vg-faset/core/styles/export-js.scss";
const Styles = require("@vg-faset/core/styles/export-js.scss");

const CHARS_MD = 50;
const CHARS_LG = 250;
const BREAK_MD = parseInt(Styles.break_md, 10);
const tipIDs = new UniqueId("tooltip-describer-");

/**
 * A tooltip component.
 */
export default {
	name: "Tooltip",

	mqlSmall: null,
	mqlMedium: null,

	props: {
		/**
		 * The original trigger text for the tooltip.
		 */
		text: {
			type: String,
			required: true,
			default: ""
		},

		/**
		 * The tooltip bubble text.
		 */
		message: {
			type: String,
			required: false,
			default: ""
		},

		/**
		 * Add custom class(es) to the element wrapping the tooltip trigger text.
		 */
		labelClasses: {
			type: Array,
			required: false,
			default: () => []
		},

		/**
		 * Used to pass a fixed-width override flag. This flag is used to override the min-width classes set by counting the number of characters in the tooltip. This can be used for items such as lists that have a high number of content characters but fit better in a narrow tooltip element.
		 */
		autoWidth: {
			type: String,
			required: false,
			default: ""
		},

		/**
		 * Set to `true` to force the bubble to always appear over the trigger text.
		 */
		forceUp: {
			type: Boolean
		},

		/**
		 * Set to `true` to open the tooltip message in a modal rather than bubble in small viewport.
		 */
		openModalInSmall: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			id: tipIDs.generate(),
			widthCSS: "",
			adjustOffset: false,
			alignCSS: "",
			placement: "",
			active: false,
			focusing: false,
			skipClear: false,
			isSmall: false,
			isMedium: false,
			isLarge: false
		};
	},

	computed: {
		hasCustomTooltipSlot() {
			return !!this.$slots.customTooltip;
		},

		openModalOnClick() {
			return this.openModalInSmall && this.isSmall;
		},

		hasMessage() {
			return this.message.length > 0;
		}
	},

	methods: {
		leftAlign() {
			this.alignCSS = "tooltip-bubble__left";
		},

		leftAlignOffset() {
			this.alignCSS = "tooltip-bubble__left-offset";
		},

		rightAlign() {
			this.alignCSS = "tooltip-bubble__right";
		},

		rightAlignOffset() {
			this.alignCSS = "tooltip-bubble__right-offset";
		},

		setAdjustedOffset() {
			this.adjustOffset = this.text.length < 4;
		},

		checkOffset() {
			if (
				!this.openModalOnClick &&
				(this.hasMessage || this.hasCustomTooltipSlot)
			) {
				let bubble = this.$refs.tooltip;
				let trigger = this.$refs.container;

				const bubbleRect = bubble.getBoundingClientRect();
				const parentRect = trigger.getBoundingClientRect();
				const docWidth = document.body.offsetWidth;
				const isSmall = window.innerWidth < BREAK_MD;

				// calculate the height of the sticky nav on either desktop or mobile
				let stickykNavHeight = 0;
				if (!isSmall) {
					// medium to large
					let stickNav = document.querySelector(".in-page-nav--sticky");
					stickykNavHeight = stickNav ? stickNav.offsetHeight : 0;
				} else {
					// mobile
					let stickNav = document.querySelector(
						".in-page-nav-section__header--active"
					);
					stickykNavHeight = stickNav ? stickNav.offsetHeight : 0;
				}

				// then we subtract it from the clientRect.top to get actual visible top
				// 25px - absolute position offset of the bubble
				let topCheck =
					parentRect.top - stickykNavHeight - (25 + bubbleRect.height);

				if (!this.forceUp && topCheck < 0) {
					this.placement = "tooltip-bubble__under";
				} else {
					this.placement = "";
				}

				// check if we're on mobile and if we're using the wide/wider class
				// then calculate the left position so that it doesnt go off screen
				let isWide = this.widthCSS === "--wide" || this.widthCSS === "--wider";
				if (isSmall && isWide) {
					let pad = docWidth * 0.025;
					bubble.style.left = "-" + (parentRect.left - pad) + "px";
				} else {
					bubble.style.left = "";
				}

				// Left side check
				// if is Off screen or has the potential to be off screen
				if (bubbleRect.left < 0 || parentRect.left - bubbleRect.width / 2 < 0) {
					if (this.adjustOffset) {
						this.leftAlignOffset();
					} else {
						this.leftAlign();
					}
					return;
				}

				// Right side check
				// if is Off screen on right or has the potential to be off screen
				if (
					bubbleRect.right > docWidth ||
					parentRect.right + bubbleRect.width / 2 > docWidth
				) {
					if (this.adjustOffset) {
						this.rightAlignOffset();
					} else {
						this.rightAlign();
					}
					return;
				}

				if (!isWide) {
					bubble.style.left = (parentRect.width - bubbleRect.width) / 2 + "px";
					return;
				}

				// if we're here then tooltip is no longer in danger of being off screen
				// and we can remove any of the modifier classes because they no longer
				// apply
				this.alignCSS = "";
			}
		},

		checkWidth() {
			if (!this.openModalOnClick && this.$refs.tt_container) {
				if (this.autoWidth === "auto-width") {
					// override --wide/--wider styles below if prop 'tooltipAutoWidth' is set to true
					this.widthCSS = "";
				} else if (this.$refs.tt_container.textContent.length > CHARS_LG) {
					// another even wider style
					this.widthCSS = "--wider";
				} else if (this.$refs.tt_container.textContent.length > CHARS_MD) {
					// set tooltip CSS
					this.widthCSS = "--wide";
				}
			}
		},

		onMouseEnter() {
			this.checkWidth();
			this.checkOffset();
		},

		// here's the deal.  Focus events fire once but click event fire multiple times
		// so the first time you click on it both events fire.  So we only let the
		// focus event handle the ui on that first click/focus
		onFocus() {
			this.focusing = true;
			if (this.active) {
				this.active = false;
				return;
			}
			// clearActive is async so lets set a bool so we can skip clearing for this instance
			this.skipClear = true;
			this.clearActive();
			// and turn it on
			this.active = true;
		},

		onBlur() {
			this.focusing = false;
			this.active = false;
		},

		onClick(e) {
			if (this.focusing) {
				// we don't do anything on the very first click, onFocus handles things
				this.focusing = false;
				if (e.type === "keyup" && this.active) {
					this.active = false;
				}
				return;
			}

			// subsequent clicks toggle the active class
			if (this.active) {
				if (e.type !== "keyup") {
					this.$refs.container.blur();
				}
				this.active = false;
				this.focusing = false;
			} else {
				this.active = true;
			}
		},

		onClickToOpenModal(e) {
			let TooltipModalComponent;

			if (this.hasCustomTooltipSlot) {
				const slot = this.$slots.customTooltip;

				TooltipModalComponent = Vue.component("TooltipModalComponent", {
					render: function (createElement) {
						return createElement("div", slot);
					}
				});
			} else if (this.message) {
				const message = this.message;

				TooltipModalComponent = Vue.component("TooltipModalComponent", {
					render: function (createElement) {
						return createElement("div", {
							attrs: {
								class: "tooltip-modal-container"
							},
							domProps: {
								innerHTML: message
							},
							style: {
								fontSize: "14px",
								lineHeight: "1.7142857143",
								backgroundColor: "white",
								fontWeight: "normal",
								letterSpacing: "normal",
								textTransform: "auto"
							}
						});
					}
				});
			}

			EventBus.$emit("modal:set", TooltipModalComponent);
			EventBus.$emit("modal:show", e);
		},

		// if another tooltip is active it should be blurred and removed active class
		clearActive() {
			EventBus.$emit("tooltip:clear", "component");
		},

		onTooltipClear() {
			if (this.skipClear) {
				this.skipClear = false;
				return;
			}
			this.$refs.container.blur();
			this.active = false;
			this.focusing = false;
		},

		matchMedia() {
			this.mqlSmall = window.matchMedia("(max-width: 767px)");
			this.mqlMedium = window.matchMedia(
				"(min-width: 768px) and (max-width: 1024px)"
			);
			this.setCurrentScreenSize();
		},

		setCurrentScreenSize() {
			if (this.mqlSmall.matches) {
				this.isSmall = true;
				this.isMedium = false;
				this.isLarge = false;
			} else if (this.mqlMedium.matches) {
				this.isSmall = false;
				this.isMedium = true;
				this.isLarge = false;
			} else {
				this.isSmall = false;
				this.isMedium = false;
				this.isLarge = true;
			}
		}
	},

	created() {
		if (this.hasMessage || this.hasCustomTooltipSlot) {
			this.matchMedia();
			this.mqlSmall.addListener(this.setCurrentScreenSize);
			this.mqlMedium.addListener(this.setCurrentScreenSize);
			this.checkWidth();
		}
	},

	mounted() {
		if (this.hasMessage || this.hasCustomTooltipSlot) {
			EventBus.$on("tooltip:clear", this.onTooltipClear);
			this.setAdjustedOffset();
		}
	},

	updated() {
		if (this.hasMessage || this.hasCustomTooltipSlot) {
			this.checkWidth();
			this.setAdjustedOffset();
			this.checkOffset();
		}
	},

	beforeDestroy() {
		if (this.hasMessage || this.hasCustomTooltipSlot) {
			this.mqlSmall.removeListener(this.setCurrentScreenSize);
			this.mqlMedium.removeListener(this.setCurrentScreenSize);
			EventBus.$off("tooltip:clear", this.onTooltipClear);
		}
	}
};
</script>

<style lang="scss">
// Tootips - the actual content part of them
// because the mixin replaces the title attr with this data one
[data-tooltip-trigger] {
	border-bottom: 1px dashed $color--red;
	cursor: help;
	position: relative;

	&.tooltip-modal {
		cursor: pointer;
	}

	&.tooltip-active {
		[role="tooltip"],
		.tooltip-arrow {
			display: block;
		}
	}
}

.no-touch [data-tooltip-trigger]:hover {
	[role="tooltip"],
	.tooltip-arrow {
		display: block;
	}
}

// height of the tooltip which will be used in a few places
$ttHeight: 25px;

// the bubble element, added inside the tooltip
[role="tooltip"] {
	@include p3;
	@include commonBoxShadow;

	background-color: #fff;
	border-radius: 2px;
	bottom: calc(100% + #{$ttHeight});
	display: none;
	font-weight: normal;
	letter-spacing: normal;
	padding: 24px;
	position: absolute;
	text-transform: none;
	text-transform: auto;
	z-index: 201;
	white-space: pre;

	& p {
		@include p3;
		margin: 10px 0;
	}

	&.tooltip-bubble__under {
		bottom: auto;
		top: calc(100% + #{$ttHeight});
	}

	&.\--custom-width {
		white-space: normal;
	}

	&.\--wide {
		$w: 300px;

		left: calc(50% - #{$w/2});
		min-width: $w;
		white-space: normal;
	}

	&.\--wider {
		$w: 500px;

		left: calc(50% - #{$w/2});
		min-width: $w;
		white-space: normal;
	}

	@include to-md {
		&.\--wider {
			left: calc(50% - 45vw);
			min-width: 90vw;
		}
	}

	&.tooltip-bubble__left {
		left: 0;
	}

	&.tooltip-bubble__left-offset {
		left: -14px;
	}

	&.tooltip-bubble__right {
		left: auto;
		right: 0;
	}

	&.tooltip-bubble__right-offset {
		left: auto;
		right: -12px;
	}
}

// Tooltips in tables
// always force the tooltip to pop under when they are in a <thead><th>
thead th [role="tooltip"] {
	bottom: auto;
	top: calc(100% + #{$ttHeight});
}
// adjust the arrow as well for the same reasons
thead th .tooltip-arrow {
	top: calc(100% + 13px);
	transform: translateX(-50%) translateY(-50%) rotate(180deg);
}
// for tooltips in the first column of a table, shift them to the left a
// little bit because otherwise they are kinda of cut off
th:first-child [role="tooltip"] {
	left: 4px !important;
}

// for tooltips in the last column of a table, shift them to the right a
// little bit because otherwise they are kinda of cut off
th:last-child [role="tooltip"] {
	left: auto !important;
	right: 4px;
}

// select which elements to show/hide focus outline when tabbing
.vg-is-tabbing {
	.tooltip-active {
		&.no-outline-when-tabbing {
			outline: none;
		}

		.outline-when-tabbing {
			outline-color: Highlight; // IE11
			outline-style: solid; // IE11
			outline-color: -webkit-focus-ring-color; // Webkit
			outline-style: auto; // Webkit
			outline-width: medium;
		}
	}
}
</style>
